import React, { useEffect, useState } from 'react'
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Platform, ActivityIndicator } from 'react-native'
import Constants from 'expo-constants'
import { Feather, Ionicons } from '@expo/vector-icons'
import { fetchAuthSession, getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth'
import { getConection } from '../appBar/context/HeaderContext'
import { getLanguage } from '../appBar/context/LocalizationContext'
import { useDeviceStore } from '../../redux/zustand/deviceInfoStore'
import { apiUrl } from '../../config/config'
import { getGlobalVersion, openDB } from '../../db/indexDB'
import theme from '../../../theme'
import { Image } from 'expo-image'
import { StorageAdapter } from '../../utils/AsyncStorage'
import { ASYNC_STORAGE_KEYS } from '../stores/constants/constants.storage'
import { useNavigateTabsStore } from '../../redux/zustand/navigateTabsStore'
import { useLoaderSaveStores } from '../../redux/zustand/loaderSaveStore'
import { useTermsAndConditionsStore } from '../../redux/zustand/termsAndContionsStore'
import { useFormIncidenceStore } from '../../redux/zustand/formIncedenceStore'
import { useDataPendingSendStore } from '../../redux/zustand/dataPendingSendStore'
import { useProgressSchedule } from '../../redux/zustand/progressScheduleStore'
import { useFooterApp } from '../../redux/zustand/footerAppStore'
import { RecoverPasswordView } from '../auth/RecoverPasswordView'
import { getAppVersion } from '../customUtils/AppConstants'
import { avatarPlaceholder } from '../customUtils/Constants'
import { getAuthToken, getDeviceInfo } from '@/utils/functions.utils'
import { request_get_user_profile } from '@/services/user.services'
import { getAvatarFromCache, setAvatarInCache } from '@/utils/ttlCache'
import { getImageStogeUrl } from '../imageUploader/ImageStorage'
import { useNavigation } from '../../hooks/useWebNavigation'

const expoConfig = Constants.expoConfig || { slug: '' }

/**
 * ConfigurationPanel - Panel de configuración para web
 * Muestra opciones de configuración de la app, incluyendo cerrar sesión
 */
const ConfigurationPanel = ({ onClose }) => {
    const { languageFile } = getLanguage()
    const { isConnected, hasInternet } = getConection()
    const { deviceId } = useDeviceStore()
    const [loading, setLoading] = useState(false)
    const [showRecoverPassword, setShowRecoverPassword] = useState(false)
    const [currentUsername, setCurrentUsername] = useState('')
    const navigation = useNavigation()

    // Zustand stores for state cleanup
    const { setIndexTab } = useNavigateTabsStore()
    const { resetValues } = useLoaderSaveStores()
    const clearDataFormIncidence = useFormIncidenceStore((state) => state.clearDataFormIncidence)
    const { setHavePendingData } = useDataPendingSendStore()
    const resetProgressSchedule = useProgressSchedule((state) => state.resetProgressSchedule)
    const resetTermsAndConditions = useTermsAndConditionsStore((state) => state.resetTermsAndConditions)
    const clearFooterApp = useFooterApp((state) => state.clearFooterApp)
    const [user, setUser] = useState(null)
    const statesRemoves = async () => {
        console.log('[ConfigurationPanel] Cleaning up states...')
        resetProgressSchedule()
        clearDataFormIncidence()
        resetTermsAndConditions()
        clearFooterApp() // Limpiar el menú del store
        await StorageAdapter.setItem(ASYNC_STORAGE_KEYS.CONTINUE_DOWNLOAD, 'false')
        setIndexTab(-1)
        resetValues()
        setHavePendingData(false)

        // Clear image caches
        Image.clearMemoryCache()
        Image.clearDiskCache()

        // Limpiar IndexedDB footer_app en web
        if (Platform.OS === 'web') {
            try {
                const db = await openDB()
                const tx = db.transaction(['footer_app'], 'readwrite')
                const store = tx.objectStore('footer_app')
                await store.clear()
                console.log('[ConfigurationPanel] 🧹 IndexedDB footer_app limpiado')
            } catch (error) {
                console.error('[ConfigurationPanel] Error limpiando IndexedDB:', error)
            }
        }
    }

    const handleLogout = async () => {
        console.log('[ConfigurationPanel] Logout iniciado')

        if (!isConnected || !hasInternet) {
            alert(languageFile.cant_logout || 'No puedes cerrar sesión sin conexión a internet')
            return
        }

        setLoading(true)

        try {
            const db = await openDB(true)
            const session = await fetchAuthSession({ forceRefresh: false }).catch((error) => {
                console.error('[ConfigurationPanel] Error al obtener la sesión:', error)
                return null
            })

            if (session && session.tokens) {
                const authToken = session.tokens.accessToken.toString()

                console.log('[ConfigurationPanel] Llamando a backend /oneapp/logout')
                const response = await fetch(`${apiUrl}/oneapp/logout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: authToken,
                        device_key: deviceId,
                    },
                })

                const data = await response.json()
                console.log('[ConfigurationPanel] Respuesta logout backend:', data.status)

                if (data['status'] === 1 || data['status'] === -1 || !session) {
                    console.log('[ConfigurationPanel] Logout exitoso, limpiando datos...')
                    const { logoutAndReset } = require('../../utils/logout')
                    await logoutAndReset()
                    await statesRemoves()
                    // No forzar reload; Main.jsx mostrará la pantalla de Login
                } else {
                    alert(languageFile.logout_not_available || 'No se pudo cerrar sesión')
                }
            } else {
                console.log('[ConfigurationPanel] No hay sesión activa, limpiando de todos modos...')
                const { logoutAndReset } = require('../../utils/logout')
                await logoutAndReset()
                await statesRemoves()
                // Sin recarga de página
            }
        } catch (error) {
            console.error('[ConfigurationPanel] Error en logout:', error)

            // Intentar cerrar sesión de todas formas
            try {
                const { logoutAndReset } = require('../../utils/logout')
                await logoutAndReset()
                await statesRemoves()
                // Sin recarga; Main.jsx decide
            } catch (cleanupError) {
                console.error('[ConfigurationPanel] Error en cleanup:', cleanupError)
                alert('Error al cerrar sesión. Por favor, intenta de nuevo.')
            }
        } finally {
            setLoading(false)
        }
    }

    const confirmLogout = () => {
        if (Platform.OS === 'web') {
            const confirmed = window.confirm(languageFile.logout_confirmation || '¿Estás seguro que deseas cerrar sesión?')
            if (confirmed) {
                handleLogout()
            }
        } else {
            // Para móvil, usar Alert nativo
            handleLogout()
        }
    }

    const handleRecoverPassword = async () => {
        if (Platform.OS === 'web') {
            const confirmed = window.confirm('¿Estás seguro que deseas cambiar tu contraseña?')
            if (confirmed) {
                try {
                    // const { getCurrentUser } = require('aws-amplify/auth')
                    const user = await getCurrentUser()
                    console.log('USUARIO ===>', user)
                    setCurrentUsername(user.username)
                    setShowRecoverPassword(true)
                } catch (error) {
                    console.error('Error getting current user:', error)
                }
            }
        }
    }

    const handleEditProfile = async () => {
        try {
            if (user) {
                navigation.navigate('EditProfile', { user_data: user })
                onClose()
            }
        } catch (error) {
            console.error('Error navigating to EditProfile:', error)
        }
    }

    useEffect(() => {
        fetchData()
    }, [])
    const fetchData = async () => {
        try {
            let data
            // console.log('🚀 ~ fetchData ~ authToken:', authToken)
            const database = await openDB()
            const currentGlobalVersion = await getGlobalVersion(database, 'user')
            const authToken = await getAuthToken()
            if (!authToken) return
            // authToken ya obtenido por getAuthToken
            const device_param = {
                device_key: deviceId,
                token_notification: '',
            }
            const headersParam = {
                version: currentGlobalVersion,
                device_key: deviceId,
                device: JSON.stringify(device_param),
                app_version: getAppVersion(), //expo.expo.android.versionCode.toString(),
                system: Platform.OS,
                deviceInfo: JSON.stringify(getDeviceInfo()),
            }
            data = await request_get_user_profile({ authToken, headersParam })

            if (data?.status === 1) {
                let user_data = {
                    ...data.single.user_data,
                    delete_active: data.single.delete_active,
                    languages: data.single.languages,
                    showClearCacheButton: data.single.showClearCacheButton ?? false,
                }

                const { email, phone } = await getEmailPhoneAttributes(user_data)

                user_data = {
                    ...user_data,
                    email: email,
                    phone: phone,
                    phone_verified: phone ? user_data.phone_verified : false,
                    email_verified: email ? user_data.email_verified : false,
                }

                console.log('USUARIO SETEADO ==>', user_data)
                setUser(user_data)
            }
        } catch (error) {
            console.error('Error al obtener el usuario autenticado:fetch data profile', error)
        }
    }
    if (showRecoverPassword) {
        return (
            <ScrollView style={styles.container}>
                <RecoverPasswordView showLogin={() => setShowRecoverPassword(false)} username={currentUsername} skipUsernameInput={true} />
                <View style={{ height: 20 }} />
            </ScrollView>
        )
    }

    const isActiveEditProfile = () => {
        const slug = expoConfig.slug
        return slug === 'shell-somosprotagonistas' || slug === 'dots' || slug === 'dots-ii'
    }

    const getEmailPhoneAttributes = async (user_data) => {
            let emailAttr = user_data['email'] || null
            let phoneAttr = user_data['phone_number'] || null
    
            try {
                const userAttributes = await fetchUserAttributes()
        
                emailAttr = userAttributes['email'] || null
                phoneAttr = userAttributes['phone_number'] || null
                
            } catch (error) {
                console.error('1Error fetching user attributes:', error)
            }
    
            return {
                email: emailAttr,
                phone: phoneAttr,
            }
        }

    return (
        <ScrollView style={styles.container}>
            <View style={styles.content}>
                {user && <PanelProfileHeader user_data={user} />}
                {/* <Text>{JSON.stringify(user)}</Text> */}
                {/* Sección: Cuenta */}
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>{languageFile.account || 'Cuenta'}</Text>
                    {/* Botón editar Perfil */}
                    {isActiveEditProfile() && (
                        <TouchableOpacity style={[styles.optionButton]} onPress={handleEditProfile} disabled={!user}>
                            <View style={styles.optionLeft}>
                                <View style={styles.iconContainer}>
                                    <Feather name='edit-2' size={20} color={theme.colors.colorPrimary} />
                                </View>
                                <Text style={styles.optionText}>{'Editar perfil'}</Text>
                            </View>
                            <Feather name='chevron-right' size={20} color={theme.colors.textSecondary} />
                        </TouchableOpacity>
                    )}
                    {/* Botón Recuperar Contraseña */}
                    <TouchableOpacity style={[styles.optionButton]} onPress={handleRecoverPassword}>
                        <View style={styles.optionLeft}>
                            <View style={styles.iconContainer}>
                                <Feather name='key' size={20} color={theme.colors.colorPrimary} />
                            </View>
                            <Text style={styles.optionText}>{'Cambiar contraseña'}</Text>
                        </View>
                        <Feather name='chevron-right' size={20} color={theme.colors.textSecondary} />
                    </TouchableOpacity>

                    {/* Botón Cerrar Sesión */}
                    <TouchableOpacity
                        style={[styles.optionButton, loading && styles.optionButtonDisabled]}
                        onPress={confirmLogout}
                        disabled={loading}
                    >
                        <View style={styles.optionLeft}>
                            <View style={[styles.iconContainer, styles.dangerIconContainer]}>
                                <Feather name='log-out' size={20} color={theme.colors.error} />
                            </View>
                            <Text style={[styles.optionText, styles.dangerText]}>{languageFile.log_out || 'Cerrar sesión'}</Text>
                        </View>
                        {loading ? (
                            <ActivityIndicator size='small' color={theme.colors.error} />
                        ) : (
                            <Feather name='chevron-right' size={20} color={theme.colors.textSecondary} />
                        )}
                    </TouchableOpacity>
                </View>

                {/* Sección: Información (placeholder para futuras opciones) */}
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>{languageFile.about || 'Acerca de'}</Text>

                    <View style={styles.infoBox}>
                        <Text style={styles.infoText}>
                            {languageFile.more_options_coming_soon || 'Más opciones de configuración estarán disponibles próximamente.'}
                        </Text>
                    </View>
                </View>
            </View>
        </ScrollView>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background || '#FFFFFF',
    },
    content: {
        padding: 20,
    },
    section: {
        marginBottom: 32,
    },
    sectionTitle: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.textSecondary || '#666',
        marginBottom: 12,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    optionButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 16,
        paddingHorizontal: 16,
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.border || '#E5E5E5',
        marginBottom: 8,
    },
    optionButtonDisabled: {
        opacity: 0.6,
    },
    optionLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    iconContainer: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: theme.colors.backgroundSecondary || '#F5F5F5',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
    },
    dangerIconContainer: {
        backgroundColor: '#FFEBEE',
    },
    optionText: {
        fontSize: 16,
        color: theme.colors.textPrimary || '#000',
        fontWeight: '500',
    },
    dangerText: {
        color: theme.colors.error || '#D32F2F',
    },
    infoBox: {
        padding: 16,
        backgroundColor: '#F5F5F5',
        borderRadius: 12,
        borderLeftWidth: 4,
        borderLeftColor: theme.colors.colorPrimary,
    },
    infoText: {
        fontSize: 14,
        color: theme.colors.textSecondary || '#666',
        lineHeight: 20,
    },
})

export default ConfigurationPanel

export const PanelProfileHeader = ({ user_data }) => {
    const __commentsAvatarCache = new Map()
    const [avatar, setAvatar] = useState(avatarPlaceholder)
    useEffect(() => {
        let mounted = true
        const key = user_data.avatar
        const hit = key ? __commentsAvatarCache.get(key) : null
        if (hit) {
            setAvatar(hit)
            return
        }
        if (key) {
            // 1) TTL cache persistente
            getAvatarFromCache(key)
                .then((cached) => {
                    if (!mounted) return
                    if (cached) {
                        setAvatar(cached)
                        return true
                    }
                    return false
                })
                .then((servedFromCache) => {
                    if (servedFromCache) return
                    // 2) Resolver si no hubo hit
                    getImageStogeUrl(key).then(async (url) => {
                        if (!mounted) return
                        if (url) {
                            setAvatar(url)
                            try {
                                await setAvatarInCache(key, url)
                            } catch {}
                        }
                    })
                })
        }
        return () => {
            mounted = false
        }
    }, [])
    return (
        <View
            style={{
                width: '100%',
                flexDirection: 'row',
                alignSelf: 'center',
                marginBottom: 15,
            }}
        >
            <TouchableOpacity onPress={() => {}}>
                <Image
                    style={[
                        {
                            aspectRatio: 1,
                            width: 60,
                            borderRadius: 30,
                        },
                        { resizeMode: 'cover' },
                    ]}
                    source={avatar}
                />
                {/* <View
                    style={{
                        zIndex: 2,
                        bottom: -12,
                        right: -12,
                        alignSelf: 'flex-end',
                        borderWidth: 1.0,
                        borderColor: 'white',
                        backgroundColor: 'white',
                        position: 'absolute',
                        borderRadius: 30,
                        alignItems: 'center',
                    }}
                >
                    <Ionicons
                        name='add-circle'
                        size={20}
                        color={theme.colors.colorPrimary}
                        style={{
                            zIndex: 2,
                            alignSelf: 'center',
                            marginLeft: 2,
                        }}
                    />
                </View> */}
            </TouchableOpacity>

            <View
                style={{
                    flex: 1,
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    marginLeft: 15,
                    marginRight: 15,
                    marginBottom: 0,
                    marginTop: 0,
                }}
            >
                <Text
                    style={{
                        fontSize: 19,
                        fontWeight: 'bold',
                        alignItems: 'flex-start',
                        color: theme.colors.textPrimary,
                        marginBottom: 0,
                    }}
                >
                    {user_data?.name} {user_data?.last_name}
                </Text>
                <Text
                    style={{
                        fontSize: 15,
                        fontWeight: 'regular',
                        color: theme.colors.textPrimary,
                    }}
                >
                    {user_data?.email && user_data?.email != 'a@a.com' ? user_data?.email : ''}
                </Text>

                <Text
                    style={{
                        fontSize: 15,
                        fontWeight: 'regular',
                        color: theme.colors.textPrimary,
                    }}
                >
                    {getAppVersion()}
                </Text>
            </View>
        </View>
    )
}
